import { plateFont, numFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  Button,
  Chip,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  IconButton,
  TextField,
  Snackbar,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SpeedIcon from '@mui/icons-material/Speed';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils';
import OdometerHistoryTab from './OdometerHistoryTab';
import RecordOdometerDialog from '../maintenances/RecordOdometerDialog';
import ExtraLookupConfirmDialog, { shouldSkipExtraLookupConfirm } from './ExtraLookupConfirmDialog';
import { InternalNoteEditor } from '../shared/InternalNote';
import { VehicleServiceInfoEditor } from '../shared/VehicleServiceInfo';
import usePermissions from '../../hooks/usePermissions';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vehicle-tabpanel-${index}`}
      aria-labelledby={`vehicle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// Info Section component for displaying vehicle data
function InfoSection({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Box>
  );
}

// Info Item component - only renders if value is present (preserves 0)
function InfoItem({ label, value, icon, xs = 6, sm = 4 }) {
  if (!value && value !== 0 && value !== false) return null;

  return (
    <Grid item xs={xs} sm={sm}>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: 'text.disabled' }}>{icon}</Box>}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {value}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
}

// Vehicle category EU translation mapping
const VEHICLE_CATEGORY_MAP = {
  M1: 'vehicle.categoryM1',
  M1G: 'vehicle.categoryM1G',
  M2: 'vehicle.categoryM2',
  M3: 'vehicle.categoryM3',
  N1: 'vehicle.categoryN1',
  N1G: 'vehicle.categoryN1G',
  N2: 'vehicle.categoryN2',
  N3: 'vehicle.categoryN3',
  L1e: 'vehicle.categoryL1e',
  L3e: 'vehicle.categoryL3e',
  L5e: 'vehicle.categoryL5e',
  L6e: 'vehicle.categoryL6e',
  L7e: 'vehicle.categoryL7e',
  T: 'vehicle.categoryT',
  C: 'vehicle.categoryC',
};

// Editable fields grouped by section
const EDIT_FIELDS = {
  basic: ['registration_number', 'manufacturer', 'model', 'model_variant', 'year_model', 'registration_date', 'model_year_from', 'model_year_to'],
  engine: ['engine_code', 'displacement_cc', 'displacement_liters', 'cylinders', 'valves', 'power_kw', 'power_hp', 'engine_type'],
  fuelDrivetrain: ['fuel', 'fuel_type', 'fuel_system', 'drive_type', 'wheel_drive', 'compressor', 'automatic'],
  body: ['body_type', 'body_code', 'doors', 'vehicle_category_eu'],
  dimensions: ['axles', 'length_mm', 'kerb_weight', 'total_road_weight', 'mass_vehicle', 'mass_trailer_with_brakes', 'mass_trailer_without_brakes', 'max_mass_combination'],
  identifiers: ['vin', 'type_code'],
  tyres: ['front_tyres', 'rear_tyres'],
};

function getInitialEditData(vehicle) {
  if (!vehicle) return {};
  return {
    // Basic
    registration_number: vehicle.registration_number || '',
    manufacturer: vehicle.manufacturer || '',
    model: vehicle.model || '',
    model_variant: vehicle.model_variant || '',
    year_model: vehicle.year_model || '',
    registration_date: vehicle.registration_date || '',
    model_year_from: vehicle.model_year_from || '',
    model_year_to: vehicle.model_year_to || '',
    // Engine
    engine_code: vehicle.engine_code || '',
    displacement_cc: vehicle.displacement_cc || '',
    displacement_liters: vehicle.displacement_liters || '',
    cylinders: vehicle.cylinders || '',
    valves: vehicle.valves || '',
    power_kw: vehicle.power_kw || '',
    power_hp: vehicle.power_hp || '',
    engine_type: vehicle.engine_type || '',
    // Fuel & drivetrain
    fuel: vehicle.fuel || '',
    fuel_type: vehicle.fuel_type || '',
    fuel_system: vehicle.fuel_system || '',
    drive_type: vehicle.drive_type || '',
    wheel_drive: vehicle.wheel_drive || '',
    compressor: vehicle.compressor ?? '',
    automatic: vehicle.automatic ?? '',
    // Body
    body_type: vehicle.body_type || '',
    body_code: vehicle.body_code || '',
    doors: vehicle.doors || '',
    vehicle_category_eu: vehicle.vehicle_category_eu || '',
    // Dimensions
    axles: vehicle.axles || '',
    length_mm: vehicle.length_mm || '',
    kerb_weight: vehicle.kerb_weight || '',
    total_road_weight: vehicle.total_road_weight || '',
    mass_vehicle: vehicle.mass_vehicle || '',
    mass_trailer_with_brakes: vehicle.mass_trailer_with_brakes || '',
    mass_trailer_without_brakes: vehicle.mass_trailer_without_brakes || '',
    max_mass_combination: vehicle.max_mass_combination || '',
    // Identifiers
    vin: vehicle.vin || '',
    type_code: vehicle.type_code || '',
    // Tyres
    front_tyres: vehicle.front_tyres || '',
    rear_tyres: vehicle.rear_tyres || '',
  };
}

// Edit Form Section component
function EditFormSection({ title, fields, editData, handleEditChange, handleBooleanChange, t, fieldLabels, fieldTypes, booleanFields }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Grid container spacing={2}>
        {fields.map((field) => {
          if (booleanFields && booleanFields.includes(field)) {
            const value = editData[field];
            return (
              <Grid item xs={6} sm={4} key={field}>
                <TextField
                  label={fieldLabels[field] || field}
                  value={value === true ? 'true' : value === false ? 'false' : ''}
                  onChange={handleBooleanChange(field)}
                  fullWidth
                  size="small"
                  select
                  SelectProps={{ native: true }}
                >
                  <option value="">{t('Not set')}</option>
                  <option value="true">{t('Yes')}</option>
                  <option value="false">{t('No')}</option>
                </TextField>
              </Grid>
            );
          }
          return (
            <Grid item xs={6} sm={4} key={field}>
              <TextField
                label={fieldLabels[field] || field}
                value={editData[field] ?? ''}
                onChange={handleEditChange(field)}
                fullWidth
                size="small"
                type={fieldTypes[field] || 'text'}
                InputLabelProps={fieldTypes[field] === 'date' ? { shrink: true } : undefined}
                inputProps={field === 'vin' ? { maxLength: 17, style: { ...numFont } } : undefined}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

// Vehicle Info Tab Component with 4 grouped sections, only showing non-empty fields/sections
function VehicleInfoTab({ vehicle, t, isEditing, editData, handleEditChange, handleBooleanChange, saveError }) {
  if (!vehicle) return null;

  const fieldLabels = {
    // Basic
    registration_number: t('Registration'),
    manufacturer: t('Manufacturer'),
    model: t('Model'),
    model_variant: t('Variant'),
    year_model: t('Year'),
    registration_date: t('Registration date'),
    model_year_from: t('vehicle.modelYearFrom'),
    model_year_to: t('vehicle.modelYearTo'),
    // Engine
    engine_code: t('Engine code'),
    displacement_cc: t('Displacement') + ' (cc)',
    displacement_liters: t('Displacement') + ' (l)',
    cylinders: t('Cylinders'),
    valves: t('Valves'),
    power_kw: t('Power') + ' (kW)',
    power_hp: t('Power') + ' (hp)',
    engine_type: t('vehicle.engineType'),
    // Fuel & drivetrain
    fuel: t('Fuel'),
    fuel_type: t('vehicle.fuelType'),
    fuel_system: t('vehicle.fuelSystem'),
    drive_type: t('vehicle.driveType'),
    wheel_drive: t('vehicle.wheelDrive'),
    compressor: t('vehicle.compressor'),
    automatic: t('vehicle.automatic'),
    // Body
    body_type: t('vehicle.bodyType'),
    body_code: t('vehicle.bodyCode'),
    doors: t('vehicle.doors'),
    vehicle_category_eu: t('vehicle.category'),
    // Dimensions
    axles: t('vehicle.axles'),
    length_mm: t('vehicle.length') + ' (mm)',
    kerb_weight: t('vehicle.curbWeight') + ' (kg)',
    total_road_weight: t('vehicle.grossWeight') + ' (kg)',
    mass_vehicle: t('vehicle.massVehicle') + ' (kg)',
    mass_trailer_with_brakes: t('vehicle.massTrailerBrakes') + ' (kg)',
    mass_trailer_without_brakes: t('vehicle.massTrailerNoBrakes') + ' (kg)',
    max_mass_combination: t('vehicle.maxMassCombination') + ' (kg)',
    // Identifiers
    vin: t('VIN'),
    type_code: t('vehicle.typeApproval'),
    // Tyres
    front_tyres: t('vehicle.frontTyres'),
    rear_tyres: t('vehicle.rearTyres'),
  };

  const fieldTypes = {
    year_model: 'number',
    model_year_from: 'number',
    model_year_to: 'number',
    registration_date: 'date',
    displacement_cc: 'number',
    displacement_liters: 'number',
    cylinders: 'number',
    valves: 'number',
    power_kw: 'number',
    power_hp: 'number',
    doors: 'number',
    axles: 'number',
    length_mm: 'number',
    kerb_weight: 'number',
    total_road_weight: 'number',
    mass_vehicle: 'number',
    mass_trailer_with_brakes: 'number',
    mass_trailer_without_brakes: 'number',
    max_mass_combination: 'number',
  };

  const booleanFields = ['compressor', 'automatic'];

  if (isEditing) {
    const sectionProps = {
      editData,
      handleEditChange,
      handleBooleanChange,
      t,
      fieldLabels,
      fieldTypes,
      booleanFields,
    };
    return (
      <Box>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
        )}
        <EditFormSection title={t('vehicle.basicInfo')} fields={EDIT_FIELDS.basic} {...sectionProps} />
        <EditFormSection title={t('vehicle.engine')} fields={EDIT_FIELDS.engine} {...sectionProps} />
        <EditFormSection title={t('Fuel & Drivetrain')} fields={EDIT_FIELDS.fuelDrivetrain} {...sectionProps} />
        <EditFormSection title={t('vehicle.bodyAndClassification')} fields={EDIT_FIELDS.body} {...sectionProps} />
        <EditFormSection title={t('vehicle.dimensions')} fields={EDIT_FIELDS.dimensions} {...sectionProps} />
        <EditFormSection title={t('vehicle.identifiers')} fields={EDIT_FIELDS.identifiers} {...sectionProps} />
        <EditFormSection title={t('Tyres')} fields={EDIT_FIELDS.tyres} {...sectionProps} />
      </Box>
    );
  }

  // Translate vehicle category
  const categoryKey = vehicle.vehicle_category_eu ? VEHICLE_CATEGORY_MAP[vehicle.vehicle_category_eu] : null;
  const translatedCategory = categoryKey ? t(categoryKey) : vehicle.vehicle_category_eu;

  // Computed values
  const displacementDisplay = vehicle.displacement_cc
    ? `${vehicle.displacement_cc} cc (${(vehicle.displacement_cc / 1000).toFixed(1)} l)`
    : null;
  const powerDisplay = vehicle.power_kw
    ? `${vehicle.power_kw} kW${vehicle.power_hp ? ` (${vehicle.power_hp} hp)` : ''}`
    : null;
  const weightCurb = vehicle.kerb_weight ? `${vehicle.kerb_weight} kg` : null;
  const weightGross = vehicle.total_road_weight ? `${vehicle.total_road_weight} kg` : null;
  const lengthDisplay = vehicle.length_mm ? `${vehicle.length_mm} mm` : null;

  // Check if sections have any data
  const hasBasicInfo = vehicle.registration_number || vehicle.manufacturer || vehicle.model ||
    vehicle.model_variant || vehicle.year_model || vehicle.body_type ||
    vehicle.vehicle_category_eu || vehicle.registration_date || vehicle.model_year_from || vehicle.model_year_to;

  const hasEngine = vehicle.engine_code || vehicle.displacement_cc || vehicle.cylinders ||
    vehicle.power_kw || vehicle.fuel || vehicle.fuel_type || vehicle.fuel_system || vehicle.engine_type ||
    vehicle.drive_type || vehicle.wheel_drive || vehicle.compressor != null;

  const hasDimensions = vehicle.kerb_weight || vehicle.total_road_weight ||
    vehicle.length_mm || vehicle.doors || vehicle.axles;

  const hasIdentifiers = vehicle.vin || vehicle.type_code;

  const hasTyres = vehicle.front_tyres || vehicle.rear_tyres;

  return (
    <Box>
      {hasBasicInfo && (
        <InfoSection title={t('vehicle.basicInfo')}>
          <InfoItem label={t('Registration')} value={vehicle.registration_number} icon={<DirectionsCarIcon fontSize="small" />} />
          <InfoItem label={t('Manufacturer')} value={vehicle.manufacturer} />
          <InfoItem label={t('Model')} value={vehicle.model} />
          <InfoItem label={t('Variant')} value={vehicle.model_variant} />
          <InfoItem label={t('Year')} value={vehicle.year_model} />
          <InfoItem label={t('vehicle.bodyType')} value={vehicle.body_type} />
          <InfoItem label={t('vehicle.category')} value={translatedCategory} />
          <InfoItem label={t('Registration date')} value={vehicle.registration_date} />
          <InfoItem label={t('vehicle.modelYearFrom')} value={vehicle.model_year_from} />
          <InfoItem label={t('vehicle.modelYearTo')} value={vehicle.model_year_to} />
        </InfoSection>
      )}

      {hasEngine && (
        <InfoSection title={t('vehicle.engine')}>
          <InfoItem label={t('Engine code')} value={vehicle.engine_code} />
          <InfoItem label={t('Displacement')} value={displacementDisplay} />
          <InfoItem label={t('Cylinders')} value={vehicle.cylinders} />
          <InfoItem label={t('Power')} value={powerDisplay} icon={<SpeedIcon fontSize="small" />} />
          <InfoItem label={t('Fuel')} value={vehicle.fuel} icon={<LocalGasStationIcon fontSize="small" />} />
          <InfoItem label={t('vehicle.fuelType')} value={vehicle.fuel_type} />
          <InfoItem label={t('vehicle.fuelSystem')} value={vehicle.fuel_system} />
          <InfoItem label={t('vehicle.driveType')} value={vehicle.drive_type} />
          <InfoItem label={t('vehicle.wheelDrive')} value={vehicle.wheel_drive} />
          <InfoItem label={t('vehicle.compressor')} value={vehicle.compressor === true ? t('Yes') : vehicle.compressor === false ? t('No') : null} />
          <InfoItem label={t('vehicle.engineType')} value={vehicle.engine_type} />
        </InfoSection>
      )}

      {hasDimensions && (
        <InfoSection title={t('vehicle.dimensions')}>
          <InfoItem label={t('vehicle.curbWeight')} value={weightCurb} />
          <InfoItem label={t('vehicle.grossWeight')} value={weightGross} />
          <InfoItem label={t('vehicle.length')} value={lengthDisplay} />
          <InfoItem label={t('vehicle.doors')} value={vehicle.doors} />
          <InfoItem label={t('vehicle.axles')} value={vehicle.axles} />
        </InfoSection>
      )}

      {hasIdentifiers && (
        <InfoSection title={t('vehicle.identifiers')}>
          <InfoItem label={t('VIN')} value={vehicle.vin} xs={12} sm={6} />
          <InfoItem label={t('vehicle.typeApproval')} value={vehicle.type_code} xs={12} sm={6} />
        </InfoSection>
      )}

      {hasTyres && (
        <InfoSection title={t('Tyres')}>
          <InfoItem label={t('vehicle.frontTyres')} value={vehicle.front_tyres} xs={12} sm={6} />
          <InfoItem label={t('vehicle.rearTyres')} value={vehicle.rear_tyres} xs={12} sm={6} />
        </InfoSection>
      )}
    </Box>
  );
}

// Maintenance History Tab Component
function MaintenanceHistoryTab({ vehicleId, t, onMaintenanceClick }) {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMaintenances = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/maintenances/?vehicle_id=${vehicleId}&ordering=-id&state=completed,picked_up`);
        const data = response.data;
        setMaintenances(Array.isArray(data) ? data : data.results || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (vehicleId) {
      fetchMaintenances();
    }
  }, [vehicleId]);

  const getStateColor = (state) => {
    switch (state) {
      case 'planned': return 'default';
      case 'in_progress': return 'primary';
      case 'completed': return 'success';
      case 'picked_up': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (maintenances.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('vehicles.noMaintenances')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {maintenances.map((maintenance) => (
        <Paper
          key={maintenance.id}
          variant="outlined"
          onClick={() => onMaintenanceClick(maintenance.id)}
          sx={{
            p: 2,
            mb: 1.5,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'primary.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  ...numFont,
                  fontSize: '14px',
                }}
              >
                #{maintenance.work_number}
              </Typography>
              <Chip
                label={t(maintenance.state_display || maintenance.state)}
                size="small"
                color={getStateColor(maintenance.state)}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
              <EventIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {formatDate(maintenance.created_at)?.split(' ')[0]}
              </Typography>
            </Box>
          </Box>

          {maintenance.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {maintenance.description}
            </Typography>
          )}

          {maintenance.customer && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {maintenance.customer.name}
              </Typography>
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
}

function VehicleDetailsDialog({ open, onClose, vehicleId, onVehicleDeleted, onVehicleUpdated, initialTab }) {
  const { t, i18n } = useTranslation();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [odometerDialogOpen, setOdometerDialogOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Lookup state
  const [lookupLoading, setLookupLoading] = useState(false);
  const [extraLookupDialogOpen, setExtraLookupDialogOpen] = useState(false);
  const [extraLookupQuotaInfo, setExtraLookupQuotaInfo] = useState(null);
  const [isTrial, setIsTrial] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchVehicle = useCallback(async () => {
    if (!vehicleId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/vehicles/${vehicleId}/`);
      setVehicle(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (open && vehicleId) {
      fetchVehicle();
      setTab(initialTab || 0);
      setIsEditing(false);
      setSaveError(null);
    }
  }, [open, vehicleId, fetchVehicle]);

  // Initialize edit data when vehicle loads
  useEffect(() => {
    if (vehicle) {
      setEditData(getInitialEditData(vehicle));
    }
  }, [vehicle]);

  const handleMaintenanceClick = (maintenanceId) => {
    onClose();
    navigate(`/maintenances/${maintenanceId}`);
  };

  const handleDownloadServiceHistory = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`/api/vehicles/${vehicleId}/service-history-pdf/`, {
        params: { language: i18n.language || 'fi' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download',
        `Huoltohistoria_${vehicle?.registration_number || 'ajoneuvo'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: t('vehicles.serviceHistoryPdfError'), severity: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Edit handlers
  const [isEditDirty, setIsEditDirty] = useState(false);

  const handleEditChange = (field) => (e) => {
    setEditData((prev) => ({ ...prev, [field]: e.target.value }));
    setIsEditDirty(true);
  };

  const handleBooleanChange = (field) => (e) => {
    const val = e.target.value;
    setEditData((prev) => ({
      ...prev,
      [field]: val === 'true' ? true : val === 'false' ? false : null,
    }));
    setIsEditDirty(true);
  };

  const handleSaveVehicle = async () => {
    if (!vehicle?.id) return;
    setSaveLoading(true);
    setSaveError(null);
    try {
      const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, editData);
      setVehicle(response.data);
      setIsEditing(false);
      setIsEditDirty(false);
      setSnackbar({ open: true, message: t('vehicles.vehicleUpdated'), severity: 'success' });
      if (onVehicleUpdated) {
        onVehicleUpdated();
      }
      return true;
    } catch (err) {
      setSaveError(t('Failed to save vehicle info'));
      // Return false rather than throw: this is also the button's onClick, where a
      // rejected promise nobody awaits becomes an unhandled rejection.
      return false;
    } finally {
      setSaveLoading(false);
    }
  };

  // Only the note is sent, so the vehicle stays eligible for the automatic registry lookup.
  const handleSaveInternalNote = async (text) => {
    const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, { internal_note: text });
    setVehicle(response.data);
    if (onVehicleUpdated) {
      onVehicleUpdated();
    }
  };

  // Same for the service info: sent alone, it does not mark the vehicle data manually edited.
  const handleSaveServiceInfo = async (text) => {
    const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, { service_info: text });
    setVehicle(response.data);
    if (onVehicleUpdated) {
      onVehicleUpdated();
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsEditDirty(false);
    setSaveError(null);
    if (vehicle) {
      setEditData(getInitialEditData(vehicle));
    }
  };

  // Lookup handler - refresh vehicle data from Biluppgifter
  const handleRefreshLookup = async (confirmExtra = false) => {
    if (!vehicle?.id) return;
    setLookupLoading(true);
    try {
      const payload = confirmExtra ? { confirm_extra: true } : {};
      const response = await axios.post(`/api/vehicles/${vehicle.id}/refresh-lookup/`, payload);
      if (response.data.success) {
        setVehicle(response.data.vehicle);
        setSnackbar({ open: true, message: t('vehicles.lookupSuccess'), severity: 'success' });
        if (onVehicleUpdated) {
          onVehicleUpdated();
        }
      } else {
        setSnackbar({
          open: true,
          message: response.data.error || t('vehicles.lookupFailed'),
          severity: 'error',
        });
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.quota_exceeded) {
        if (data.is_trial) {
          setSnackbar({
            open: true,
            message: data.error || t('vehicles.lookupFailed'),
            severity: 'error',
          });
        } else if (!confirmExtra && !shouldSkipExtraLookupConfirm()) {
          // Show confirmation dialog for over-quota paid lookup
          setExtraLookupQuotaInfo(data.quota_info);
          setIsTrial(false);
          setExtraLookupDialogOpen(true);
        } else {
          // Auto-confirmed (user opted to skip dialog) — retry with confirm_extra
          handleRefreshLookup(true);
          return; // Don't clear loading yet
        }
      } else {
        const errorMsg = data?.error || t('vehicles.lookupFailed');
        setSnackbar({ open: true, message: errorMsg, severity: 'error' });
      }
    } finally {
      setLookupLoading(false);
    }
  };

  // Delete handler
  const handleDeleteVehicle = async () => {
    if (!vehicle?.id) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/api/vehicles/${vehicle.id}/`);
      setDeleteDialogOpen(false);
      onClose();
      if (onVehicleDeleted) {
        onVehicleDeleted();
      }
    } catch (err) {
      setSnackbar({ open: true, message: t('Failed to delete vehicle'), severity: 'error' });
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Unsaved changes protection for dialog close (backdrop click / Escape)
  const handleSaveVehicleRef = useRef(handleSaveVehicle);
  handleSaveVehicleRef.current = handleSaveVehicle;

  const { guardedOnClose, UnsavedChangesDialog: VehicleUnsavedDialog } = useUnsavedChanges({
    hasChanges: isEditing && isEditDirty && open,
    onSave: () => handleSaveVehicleRef.current(),
    mode: 'dialog',
    onClose,
  });

  if (!vehicleId) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={guardedOnClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '50vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: 'rgba(49, 130, 206, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'secondary.main',
              }}
            >
              <DirectionsCarIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ ...plateFont }}>
                {vehicle?.registration_number || '...'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicle ? `${vehicle.manufacturer} ${vehicle.model}` : '...'}
                {vehicle?.year_model && ` (${vehicle.year_model})`}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab label={t('vehicles.vehicleInfo')} icon={<DirectionsCarIcon />} iconPosition="start" />
            <Tab label={t('vehicles.maintenanceHistory')} icon={<BuildIcon />} iconPosition="start" />
            <Tab label={t('odometer.history')} icon={<SpeedIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              <TabPanel value={tab} index={0}>
                {vehicle && (
                  <InternalNoteEditor
                    kind="vehicle"
                    note={vehicle.internal_note}
                    canEdit={can('vehicles.manage')}
                    onSave={handleSaveInternalNote}
                    sx={{ mb: 2 }}
                  />
                )}
                {vehicle && (
                  <VehicleServiceInfoEditor
                    serviceInfo={vehicle.service_info}
                    canEdit={can('vehicles.manage')}
                    onSave={handleSaveServiceInfo}
                    sx={{ mb: 2 }}
                  />
                )}
                {/* Edit/Save/Cancel/Lookup buttons */}
                {!isEditing ? (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Tooltip title={t('vehicles.lookupTooltip')} arrow>
                      <span>
                        <Button
                          size="small"
                          startIcon={lookupLoading ? <CircularProgress size={16} /> : <SearchIcon />}
                          onClick={() => handleRefreshLookup()}
                          disabled={lookupLoading || !vehicle?.registration_number}
                          sx={{ color: 'text.secondary' }}
                        >
                          {t('vehicles.lookupData')}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditing(true)}
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('Edit')}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSaveVehicle}
                      disabled={saveLoading}
                    >
                      {t('Save')}
                    </Button>
                  </Box>
                )}
                <VehicleInfoTab
                  vehicle={vehicle}
                  t={t}
                  isEditing={isEditing}
                  editData={editData}
                  handleEditChange={handleEditChange}
                  handleBooleanChange={handleBooleanChange}
                  saveError={saveError}
                />
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Button
                    size="small"
                    startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                    onClick={handleDownloadServiceHistory}
                    disabled={downloadingPdf || !vehicle}
                    data-testid="download-service-history-pdf"
                  >
                    {t('vehicles.downloadServiceHistory')}
                  </Button>
                </Box>
                <MaintenanceHistoryTab
                  vehicleId={vehicleId}
                  t={t}
                  onMaintenanceClick={handleMaintenanceClick}
                />
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <OdometerHistoryTab
                  vehicleId={vehicleId}
                  onRecordOdometer={() => setOdometerDialogOpen(true)}
                />
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            disabled={!vehicle}
            size="small"
          >
            {t('Delete Vehicle')}
          </Button>
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogActions>

        <RecordOdometerDialog
          open={odometerDialogOpen}
          handleClose={() => {
            setOdometerDialogOpen(false);
            // Refresh vehicle data to get latest odometer
            fetchVehicle();
          }}
          vehicleId={vehicleId}
          currentOdometer={vehicle?.latest_odometer}
          mode="vehicle"
        />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('Delete Vehicle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete this vehicle?')}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            {t('vehicles.deleteVehicleWarning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleDeleteVehicle}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extra Lookup Confirmation Dialog (over-quota) */}
      <ExtraLookupConfirmDialog
        open={extraLookupDialogOpen}
        onClose={() => setExtraLookupDialogOpen(false)}
        onConfirm={() => {
          setExtraLookupDialogOpen(false);
          handleRefreshLookup(true);
        }}
        quotaInfo={extraLookupQuotaInfo}
        isTrial={isTrial}
      />

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {VehicleUnsavedDialog}
    </>
  );
}

export default VehicleDetailsDialog;
